import { colors } from '@opendoor/bricks/theme/eero';
import { useObservability } from '@opendoor/observability';
import * as api from '../api';
import { Box, Flex, Text, Image } from '@opendoor/bricks/core';
import { UNSAFE_CtaButton } from '@opendoor/bricks/deprecated/UNSAFE_CtaButton';
import imgixUrl from '../../helpers/imgixUrl';
import { COSMOS_URL, CONSUMER_FE_URL } from '../globals';
import { ReturnExperienceData } from '../../pages/homepage-2019';
import { useState } from 'react';

export const OFFER_EXPIRY_DAYS_CUTOFF = 4;

export enum ExperienceType {
  offer_expired,
  incomplete_flow,
  offer_expiring,
  offer_decreased,
  offer_increased,
  active_offer,
  multiple_properties,
}

type HeadlineTextProps = {
  firstLine: string;
  secondLine: JSX.Element | string;
  address?: string;
};

type HeadlineComponentProps = {
  experience: ExperienceType;
  firstName?: string;
  address?: string;
  daysToExpire?: number;
};

type OfferDetailsProps = {
  experience: ExperienceType;
  sellerInputUUID: string;
  currentOfferPriceCents?: number;
  previousOfferPriceCents?: number;
  offerRequestSource?: string;
  customerUUID?: string;
  addressToken?: string;
};

const HeadlineText = (props: HeadlineTextProps) => (
  <Box color="neutrals100" margin="auto" pl={[null, null, null, 6]}>
    <Box display={[null, null, null, 'none']} pt={[3, null, null, 'initial']}>
      <Text fontWeight="bolder" lineHeight={[1.2, null, 1.45]}>
        {props.firstLine}
      </Text>
      <Text fontWeight="bolder" lineHeight={[1.2, null, 1.45]}>
        {props.secondLine}
      </Text>
    </Box>
    <Text
      display={['none', null, null, 'initial']}
      fontWeight="bolder"
      lineHeight={[1.2, null, 1.45]}
      pt={[3, null, null, 'initial']}
    >
      {props.firstLine + ' '}
      {props.secondLine}
    </Text>
    {props?.address && <Text>{props.address}</Text>}
  </Box>
);

const HeadlineComponent = (props: HeadlineComponentProps): JSX.Element | null => {
  switch (props.experience) {
    case ExperienceType.multiple_properties:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          fontSize="s0"
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
          borderBottom={[`1px solid ${colors.neutrals40}`, null, null, 'initial']}
        >
          <Image
            src={imgixUrl('5970e34ec121477b9089e6e18e202ff5.png')}
            alt=""
            alignSelf="center"
            width="40px"
            mx="auto"
          />
          <HeadlineText
            firstLine={`Welcome back, ${props.firstName}!`}
            secondLine="Still thinking of selling?"
          />
        </Flex>
      );
    case ExperienceType.offer_expired:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          fontSize="s0"
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
        >
          <Image
            src={imgixUrl('56cb13553eca44b588b821683c98241d.png')}
            alt=""
            alignSelf="center"
            width="40px"
            mx="auto"
          />
          <HeadlineText
            firstLine={`Welcome back, ${props.firstName}!`}
            secondLine={'Your offer is expired. Refresh for an updated offer.'}
            address={props.address}
          />
        </Flex>
      );
    case ExperienceType.incomplete_flow:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          fontSize="s0"
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
        >
          <Image
            src={imgixUrl('56cb13553eca44b588b821683c98241d.png')}
            alt=""
            alignSelf="center"
            width="40px"
            mx="auto"
          />
          <HeadlineText
            firstLine={'Welcome back!'}
            secondLine={'Take a minute to complete your offer on'}
            address={props.address}
          />
        </Flex>
      );
    case ExperienceType.offer_expiring:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          fontSize="s0"
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
        >
          <Image
            src={imgixUrl('56cb13553eca44b588b821683c98241d.png')}
            alt=""
            alignSelf="center"
            width="40px"
            mx="auto"
          />
          <HeadlineText
            firstLine={`Welcome back, ${props.firstName}!`}
            secondLine={
              <>
                Your preliminary offer expires{' '}
                <span color={colors.terracotta50}>
                  {props.daysToExpire && props.daysToExpire > 1
                    ? `in ${props.daysToExpire} days.`
                    : 'today.'}
                </span>
              </>
            }
            address={props.address}
          />
        </Flex>
      );
    case ExperienceType.offer_decreased:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          fontSize="s0"
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
          borderBottom={[`1px solid ${colors.neutrals40}`, null, null, 'initial']}
        >
          <Image
            src={imgixUrl('a432fc582ec04a6c83a63eced151657e.png')}
            alt=""
            alignSelf="center"
            width="40px"
            mx="auto"
          />
          <HeadlineText
            firstLine={`Welcome back, ${props.firstName}!`}
            secondLine="Your preliminary offer was updated."
            address={props.address}
          />
        </Flex>
      );
    case ExperienceType.offer_increased:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          fontSize="s0"
          px={[null, null, null, 6]}
          pt={[5, null, null, 7]}
          pb={[3, null, null, 7]}
        >
          <Image
            src={imgixUrl('8866f7d5729448cc991d1c2149ed3d28.png')}
            alt=""
            alignSelf="center"
            width="40px"
            mx="auto"
          />
          <HeadlineText
            firstLine={`Great news, ${props.firstName}!`}
            secondLine={
              <>
                Your preliminary offer has <span color={colors.green50}>increased.</span>
              </>
            }
            address={props.address}
          />
        </Flex>
      );
    case ExperienceType.active_offer:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          fontSize="s0"
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
          borderBottom={[`1px solid ${colors.neutrals40}`, null, null, 'initial']}
        >
          <Image
            src={imgixUrl('5970e34ec121477b9089e6e18e202ff5.png')}
            alt=""
            alignSelf="center"
            width="40px"
            mx="auto"
          />
          <HeadlineText
            firstLine={`Welcome back, ${props.firstName}!`}
            secondLine="Still thinking of selling?"
            address={props.address}
          />
        </Flex>
      );
    default:
      return null;
  }
};

const BenefitLineItem = (props: { text: string }) => (
  <Flex>
    <Image src={imgixUrl('5517b44c75864df491a087b692ae8f7b.svg')} alt="Checkmark icon" />
    <Text pl={2}>{props.text}</Text>
  </Flex>
);

const OfferDetails = (props: OfferDetailsProps): JSX.Element | null => {
  let offerDashUrl;
  if (props.customerUUID && props.addressToken) {
    // Return to sell tab in hub for returning customers
    offerDashUrl = `${CONSUMER_FE_URL}/dashboard/customer/${props.customerUUID}/${props.addressToken}/sell`;
  } else {
    offerDashUrl = `${CONSUMER_FE_URL}/dashboard/${props.sellerInputUUID}/overview`;
  }
  const { trackEvent } = useObservability();
  const [loading, setLoading] = useState(false);

  const fnHandleCTA = (url: string, returnExperienceType: string, sellerInputUUID?: string) => {
    return () => {
      // TODO: remove categoryOverride
      // observability is initialized with 'cosmos' as the category for all events
      trackEvent(
        'click',
        'success',
        {
          lead_token: sellerInputUUID,
          return_experience_type: returnExperienceType,
        },
        { categoryOverride: 'seller-return-experience' },
      );
      window.location.href = url;
    };
  };

  const fnHandleRefresh = (url: string, returnExperienceType: string, sellerInputUUID: string) => {
    return async () => {
      setLoading(true);
      // TODO: remove categoryOverride
      // observability is initialized with 'cosmos' as the category for all events
      trackEvent(
        'click',
        'success',
        {
          lead_token: sellerInputUUID,
          return_experience_type: returnExperienceType,
        },
        { categoryOverride: 'seller-return-experience' },
      );
      await api.refreshOffer(sellerInputUUID);
      window.location.href = url;
    };
  };

  switch (props.experience) {
    case ExperienceType.multiple_properties:
      // eslint-disable-next-line no-case-declarations
      const propertiesUrl = `${COSMOS_URL}/properties`;
      return (
        <Box
          minWidth={[null, null, null, '25%']}
          width={[0.8, null, null, 'initial']}
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
          margin={['auto', null, null, 'auto 0']}
        >
          <UNSAFE_CtaButton
            aria-label=""
            actionText="View properties"
            designStyle="rounded"
            onClick={fnHandleCTA(propertiesUrl, ExperienceType[props.experience])}
          />
        </Box>
      );
    case ExperienceType.offer_expired:
      return (
        <Box
          minWidth={[null, null, null, '25%']}
          width={[0.8, null, null, 'initial']}
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
          margin={['auto', null, null, 'auto 0']}
        >
          <UNSAFE_CtaButton
            aria-label=""
            loading={loading}
            actionText="Refresh your offer now"
            designStyle="rounded"
            onClick={fnHandleRefresh(
              offerDashUrl,
              ExperienceType[props.experience],
              props.sellerInputUUID,
            )}
          />
        </Box>
      );
    case ExperienceType.incomplete_flow:
      // eslint-disable-next-line no-case-declarations
      const sellerFlowUrl = `${CONSUMER_FE_URL}/seller-lead/${props.sellerInputUUID}`;
      return (
        <Box
          minWidth={[null, null, null, '25%']}
          width={[0.8, null, null, 'initial']}
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
          margin={['auto', null, null, 'auto 0']}
        >
          <UNSAFE_CtaButton
            aria-label=""
            actionText="Resume your offer"
            designStyle="rounded"
            onClick={fnHandleCTA(
              sellerFlowUrl,
              ExperienceType[props.experience],
              props.sellerInputUUID,
            )}
          />
        </Box>
      );
    case ExperienceType.offer_expiring:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          minWidth={[null, null, null, '40%']}
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
        >
          <Text
            color="neutrals100"
            fontSize="UNSAFE_h1Mobile"
            fontWeight="bolder"
            px={[null, null, null, 4]}
            pb={[3, null, null, 'initial']}
          >
            $
            {props.currentOfferPriceCents
              ? (props.currentOfferPriceCents / 100).toLocaleString()
              : ''}
          </Text>
          <Box flex="1 0 auto" width={[0.8, null, null, 'initial']} margin="auto">
            <UNSAFE_CtaButton
              aria-label=""
              actionText="View Offer"
              designStyle="rounded"
              onClick={fnHandleCTA(
                offerDashUrl,
                ExperienceType[props.experience],
                props.sellerInputUUID,
              )}
            />
          </Box>
        </Flex>
      );
    case ExperienceType.offer_decreased:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          minWidth={[null, null, null, '40%']}
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
        >
          <Box
            color="neutrals90"
            fontSize="s00"
            pr={[null, null, null, 7]}
            pb={[4, null, null, 'initial']}
            mx="auto"
          >
            <BenefitLineItem text="Skip showings and repairs" />
            <BenefitLineItem text="Move on your schedule" />
            <BenefitLineItem text="Get paid in a matter of days" />
          </Box>
          <Box flex="1 0 auto" width={[0.8, null, null, 'initial']} margin="auto">
            <UNSAFE_CtaButton
              aria-label=""
              actionText="View offer"
              designStyle="rounded"
              onClick={fnHandleCTA(
                offerDashUrl,
                ExperienceType[props.experience],
                props.sellerInputUUID,
              )}
            />
          </Box>
        </Flex>
      );
    case ExperienceType.offer_increased:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          justifyContent="space-around"
          minWidth={[null, null, null, '40%']}
          px={[null, null, null, 6]}
          pt={[3, null, null, 7]}
          pb={[5, null, null, 7]}
        >
          <Text
            color="neutrals100"
            fontSize="UNSAFE_h1Mobile"
            fontWeight="bolder"
            px={[null, null, null, 4]}
            pb={[3, null, null, 'initial']}
            borderBottom={[`1px solid ${colors.neutrals40}`, null, null, 'initial']}
            borderRight={[null, null, null, `1px solid ${colors.neutrals40}`]}
          >
            $
            {props.currentOfferPriceCents
              ? (props.currentOfferPriceCents / 100).toLocaleString()
              : ''}
          </Text>
          <Flex px={4} pb={[4, null, null, 'initial']} margin="auto">
            <Text color="neutrals90" fontSize="s000" fontWeight="bolder" px={4} margin="auto">
              Net proceeds
            </Text>
            <Flex px={4}>
              <Image src={imgixUrl('23cae50268d846aaa1e443e0844daf67.svg')} alt="Up arrow" />
              <Text color="green50" fontSize="s0" fontWeight="bolder">
                $
                {(
                  (props.currentOfferPriceCents! - props.previousOfferPriceCents!) /
                  100
                ).toLocaleString()}
              </Text>
            </Flex>
          </Flex>
          <Box flex="1 0 auto" width={[0.8, null, null, 'initial']} margin="auto">
            <UNSAFE_CtaButton
              aria-label=""
              actionText="View offer"
              designStyle="rounded"
              onClick={fnHandleCTA(
                offerDashUrl,
                ExperienceType[props.experience],
                props.sellerInputUUID,
              )}
            />
          </Box>
        </Flex>
      );
    case ExperienceType.active_offer:
      return (
        <Flex
          flexDirection={['column', null, null, 'initial']}
          minWidth={[null, null, null, '40%']}
          px={[null, null, null, 6]}
          py={[5, null, null, 7]}
        >
          {props.offerRequestSource == 'automated_reengagement_offer' && (
            <Text fontSize="s00" margin="auto">
              We updated your offer
            </Text>
          )}
          <Text
            color="neutrals100"
            fontSize="UNSAFE_h1Mobile"
            fontWeight="bolder"
            px={[null, null, null, 4]}
            pb={[3, null, null, 'initial']}
          >
            $
            {props.currentOfferPriceCents
              ? (props.currentOfferPriceCents / 100).toLocaleString()
              : ''}
          </Text>
          <Box flex="1 0 auto" width={[0.8, null, null, 'initial']} margin="auto">
            <UNSAFE_CtaButton
              aria-label=""
              actionText="View offer"
              designStyle="rounded"
              onClick={fnHandleCTA(
                offerDashUrl,
                ExperienceType[props.experience],
                props.sellerInputUUID,
              )}
            />
          </Box>
        </Flex>
      );
    default:
      return null;
  }
};

const ReturnExperienceBanner = (props: ReturnExperienceData) => {
  const { trackEvent } = useObservability();
  // TODO: remove categoryOverride
  // observability is initialized with 'cosmos' as the category for all events
  try {
    trackEvent(
      'view',
      'success',
      {
        lead_token: props.sellerInputUUID,
        return_experience_type: ExperienceType[props.experienceType!],
      },
      { categoryOverride: 'seller-return-experience' },
    );
  } catch (e) {
    console.error('Could not call trackEvent, did you call initObservabilty?');
  }

  return (
    <Box backgroundColor="neutrals0" marginTop="55px" paddingBottom={[5, 0]}>
      <Flex
        width={[0.75, null, 0.5, 1]}
        flexDirection={['column', null, null, 'initial']}
        justifyContent={[null, null, null, 'space-between']}
        textAlign={['center', null, null, 'initial']}
        mx="auto"
      >
        <HeadlineComponent
          experience={props.experienceType!}
          firstName={props.firstName}
          address={props.address}
          daysToExpire={props.offerExpiresInDays}
        />
        <OfferDetails
          experience={props.experienceType!}
          sellerInputUUID={props.sellerInputUUID!}
          currentOfferPriceCents={props.currentOfferHeadlinePriceCents}
          previousOfferPriceCents={props.previousOfferHeadlinePriceCents}
          offerRequestSource={props.offerRequestSource}
          customerUUID={props.customerUUID}
          addressToken={props.addressToken}
        />
      </Flex>
    </Box>
  );
};

export default ReturnExperienceBanner;
